/**
 * Unified Inventory Filters Component
 * 
 * Single source of truth for all inventory filtering across the application.
 * Supports multiple variants for different contexts (inline, modal, compact).
 * 
 * Usage:
 * - Inventory page: variant="inline"
 * - TryOn Studio modal: variant="compact"
 * - Customer TryOn Studio: variant="modal"
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, ChevronDown, ChevronUp, X,
  Palette, DollarSign, Tag, Box
} from 'lucide-react';

// ==================== Gender Toggle ====================
const GenderToggle = ({ value, onChange, size = 'default' }) => {
  const sizeClasses = size === 'small' 
    ? 'px-2.5 py-1 text-xs' 
    : 'px-3 py-1.5 text-sm';
  
  return (
    <div className="flex bg-slate-100 rounded-lg p-1 flex-shrink-0">
      <button
        onClick={() => onChange('female')}
        className={`${sizeClasses} rounded-md font-medium transition-colors ${
          value === 'female' 
            ? 'bg-pink-500 text-white shadow-sm' 
            : 'text-slate-600 hover:text-slate-800'
        }`}
        data-testid="gender-filter-women"
      >
        Women
      </button>
      <button
        onClick={() => onChange('male')}
        className={`${sizeClasses} rounded-md font-medium transition-colors ${
          value === 'male' 
            ? 'bg-blue-500 text-white shadow-sm' 
            : 'text-slate-600 hover:text-slate-800'
        }`}
        data-testid="gender-filter-men"
      >
        Men
      </button>
    </div>
  );
};

// ==================== Search Input ====================
const SearchInput = ({ value, onChange, placeholder = 'Search...', size = 'default' }) => {
  const sizeClasses = size === 'small' ? 'py-1.5 text-xs' : 'py-2 text-sm';
  
  return (
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full pl-9 pr-4 ${sizeClasses} border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
        data-testid="filter-search-input"
      />
    </div>
  );
};

// ==================== Category Pills ====================
const CategoryPills = ({ categories, value, onChange, showAll = true }) => {
  if (!categories || categories.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2">
      {showAll && (
        <button
          onClick={() => onChange('')}
          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
            !value
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          data-testid="category-filter-all"
        >
          All
        </button>
      )}
      {categories.map((cat) => (
        <button
          key={cat}
          onClick={() => onChange(cat)}
          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
            value === cat
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {cat}
        </button>
      ))}
    </div>
  );
};

// ==================== Price Range Pills ====================
const PriceRangePills = ({ value, onChange }) => {
  const ranges = [
    { label: 'All', min: 0, max: Infinity },
    { label: 'Under ₹1K', min: 0, max: 1000 },
    { label: '₹1K - ₹3K', min: 1000, max: 3000 },
    { label: '₹3K - ₹5K', min: 3000, max: 5000 },
    { label: '₹5K - ₹10K', min: 5000, max: 10000 },
    { label: '₹10K+', min: 10000, max: Infinity },
  ];
  
  const isSelected = (range) => {
    return value.min === range.min && value.max === range.max;
  };
  
  return (
    <div className="flex flex-wrap gap-2">
      {ranges.map((range) => (
        <button
          key={range.label}
          onClick={() => onChange({ min: range.min, max: range.max })}
          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
            isSelected(range)
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          data-testid={`price-filter-${range.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {range.label}
        </button>
      ))}
    </div>
  );
};

// ==================== Color Select ====================
const ColorSelect = ({ colors, value, onChange }) => {
  if (!colors || colors.length === 0) return null;
  
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white min-w-[120px]"
      data-testid="color-filter-select"
    >
      <option value="">All Colors</option>
      {colors.map((color) => (
        <option key={color} value={color}>{color}</option>
      ))}
    </select>
  );
};

// ==================== Stock Status Select ====================
const StockStatusSelect = ({ value, onChange }) => {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white min-w-[120px]"
      data-testid="stock-filter-select"
    >
      <option value="all">All Stock</option>
      <option value="in_stock">In Stock</option>
      <option value="out_of_stock">Out of Stock</option>
    </select>
  );
};

// ==================== Filter Badge Count ====================
const FilterBadge = ({ count }) => {
  if (count <= 0) return null;
  
  return (
    <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
      {count}
    </span>
  );
};

// ==================== Main Component: Inline Variant ====================
const InlineFilters = ({
  filters,
  filterOptions,
  onFilterChange,
  onSearchChange,
  onReset,
  searchQuery,
  activeFilterCount,
  showFilters: visibleFilters = ['gender', 'search', 'category', 'color', 'price', 'stock'],
  searchPlaceholder = 'Search by name, SKU...'
}) => {
  const [expanded, setExpanded] = useState(false);
  
  // Count filters excluding gender (since it's always visible)
  const additionalFilterCount = activeFilterCount - (filters.gender ? 1 : 0);
  
  return (
    <div className="space-y-3">
      {/* Primary Row: Gender, Search, Filter Toggle */}
      <div className="flex flex-col sm:flex-row gap-3">
        {visibleFilters.includes('gender') && (
          <GenderToggle 
            value={filters.gender} 
            onChange={(val) => onFilterChange('gender', val)} 
          />
        )}
        
        {visibleFilters.includes('search') && (
          <SearchInput 
            value={searchQuery} 
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
          />
        )}
        
        {/* Expandable Filters Toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center justify-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
            expanded || additionalFilterCount > 0
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
          data-testid="expand-filters-btn"
        >
          <Filter className="w-4 h-4" />
          <span className="hidden sm:inline">Filters</span>
          <FilterBadge count={additionalFilterCount} />
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      
      {/* Expanded Filters */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {visibleFilters.includes('category') && filterOptions.categories?.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-2">
                      <Tag className="w-3 h-3 inline mr-1" />Category
                    </label>
                    <select
                      value={filters.category}
                      onChange={(e) => onFilterChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    >
                      <option value="">All Categories</option>
                      {filterOptions.categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {visibleFilters.includes('color') && filterOptions.colors?.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-2">
                      <Palette className="w-3 h-3 inline mr-1" />Color
                    </label>
                    <ColorSelect 
                      colors={filterOptions.colors}
                      value={filters.color}
                      onChange={(val) => onFilterChange('color', val)}
                    />
                  </div>
                )}
                
                {visibleFilters.includes('stock') && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-2">
                      <Box className="w-3 h-3 inline mr-1" />Stock Status
                    </label>
                    <StockStatusSelect 
                      value={filters.stockStatus}
                      onChange={(val) => onFilterChange('stockStatus', val)}
                    />
                  </div>
                )}
              </div>
              
              {/* Reset Button */}
              <div className="flex justify-end">
                <button
                  onClick={onReset}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="reset-filters-btn"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== Main Component: Compact Variant ====================
const CompactFilters = ({
  filters,
  filterOptions,
  onFilterChange,
  onSearchChange,
  onPriceRangeChange,
  onReset,
  searchQuery,
  activeFilterCount,
  showFilters: visibleFilters = ['gender', 'search', 'category', 'color', 'stock', 'price'],
  searchPlaceholder = 'Search products...'
}) => {
  const [expanded, setExpanded] = useState(false);
  
  // Count filters excluding gender (since it's always visible)
  const additionalFilterCount = (activeFilterCount || 0) - (filters.gender ? 1 : 0);
  
  return (
    <div className="space-y-3">
      {/* Primary Row */}
      <div className="flex flex-col sm:flex-row gap-2">
        {visibleFilters.includes('gender') && (
          <GenderToggle 
            value={filters.gender} 
            onChange={(val) => onFilterChange('gender', val)}
            size="small"
          />
        )}
        
        {visibleFilters.includes('search') && (
          <SearchInput 
            value={searchQuery} 
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            size="small"
          />
        )}
        
        {/* Filter Toggle Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center justify-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm transition-colors flex-shrink-0 ${
            expanded || additionalFilterCount > 0
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
          data-testid="compact-expand-filters-btn"
        >
          <Filter className="w-3.5 h-3.5" />
          <span>Filters</span>
          {additionalFilterCount > 0 && (
            <span className="px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
              {additionalFilterCount}
            </span>
          )}
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>
      
      {/* Expanded Filters */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {/* Category */}
                {visibleFilters.includes('category') && filterOptions?.categories?.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1.5">Category</label>
                    <select
                      value={filters.category || ''}
                      onChange={(e) => onFilterChange('category', e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
                    >
                      <option value="">All</option>
                      {filterOptions.categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Color */}
                {visibleFilters.includes('color') && filterOptions?.colors?.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1.5">Color</label>
                    <select
                      value={filters.color || ''}
                      onChange={(e) => onFilterChange('color', e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
                    >
                      <option value="">All</option>
                      {filterOptions.colors.map(color => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Stock Status */}
                {visibleFilters.includes('stock') && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1.5">Stock</label>
                    <select
                      value={filters.stockStatus || 'all'}
                      onChange={(e) => onFilterChange('stockStatus', e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm bg-white"
                    >
                      <option value="all">All</option>
                      <option value="in_stock">In Stock</option>
                      <option value="out_of_stock">Out of Stock</option>
                    </select>
                  </div>
                )}
                
                {/* Price Range */}
                {visibleFilters.includes('price') && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1.5">Price (₹)</label>
                    <div className="flex gap-1.5">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.priceRange?.min || ''}
                        onChange={(e) => onPriceRangeChange && onPriceRangeChange('min', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.priceRange?.max || ''}
                        onChange={(e) => onPriceRangeChange && onPriceRangeChange('max', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Reset Button */}
              {additionalFilterCount > 0 && onReset && (
                <div className="flex justify-end pt-1">
                  <button
                    onClick={onReset}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Reset Filters
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== Main Component: Modal Variant ====================
const ModalFilters = ({
  isOpen,
  onClose,
  filters,
  filterOptions,
  onFilterChange,
  onApply,
  onReset,
  title = 'Filters'
}) => {
  // Local state for filters (apply on confirm)
  const [localFilters, setLocalFilters] = useState(filters);
  
  // Sync local filters when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [isOpen, filters]);
  
  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };
  
  const handleReset = () => {
    const resetFilters = {
      ...localFilters,
      gender: 'female',
      category: '',
      color: '',
      priceRange: { min: 0, max: Infinity },
      searchQuery: ''
    };
    setLocalFilters(resetFilters);
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg"
            data-testid="close-filter-modal"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 140px)' }}>
          {/* Gender Filter */}
          <div className="mb-6">
            <p className="text-sm font-medium text-slate-700 mb-3">Gender</p>
            <div className="flex bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => setLocalFilters({ ...localFilters, gender: 'female' })}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  localFilters.gender === 'female'
                    ? 'bg-white text-pink-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                Women
              </button>
              <button
                onClick={() => setLocalFilters({ ...localFilters, gender: 'male' })}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                  localFilters.gender === 'male'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                Men
              </button>
            </div>
          </div>
          
          {/* Category Filter */}
          {filterOptions.categories?.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-slate-700 mb-3">Category</p>
              <CategoryPills 
                categories={filterOptions.categories}
                value={localFilters.category}
                onChange={(val) => setLocalFilters({ ...localFilters, category: val })}
              />
            </div>
          )}
          
          {/* Search */}
          <div className="mb-6">
            <p className="text-sm font-medium text-slate-700 mb-3">Search</p>
            <input
              type="text"
              value={localFilters.searchQuery || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, searchQuery: e.target.value })}
              placeholder="Search by name or SKU..."
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Price Range */}
          <div className="mb-6">
            <p className="text-sm font-medium text-slate-700 mb-3">Price Range</p>
            <PriceRangePills 
              value={localFilters.priceRange || { min: 0, max: Infinity }}
              onChange={(val) => setLocalFilters({ ...localFilters, priceRange: val })}
            />
          </div>
          
          {/* Color Filter */}
          {filterOptions.colors?.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-slate-700 mb-3">Color</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setLocalFilters({ ...localFilters, color: '' })}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    !localFilters.color
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  All
                </button>
                {filterOptions.colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setLocalFilters({ ...localFilters, color })}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      localFilters.color === color
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="flex gap-3 p-4 border-t border-slate-200">
          <button
            onClick={handleReset}
            className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
            data-testid="reset-modal-filters"
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
            data-testid="apply-modal-filters"
          >
            Apply Filters
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==================== Main Export Component ====================
const InventoryFilters = ({
  variant = 'inline',
  ...props
}) => {
  switch (variant) {
    case 'compact':
      return <CompactFilters {...props} />;
    case 'modal':
      return <ModalFilters {...props} />;
    case 'inline':
    default:
      return <InlineFilters {...props} />;
  }
};

// Export sub-components for granular use
export { 
  GenderToggle, 
  SearchInput, 
  CategoryPills, 
  PriceRangePills,
  ColorSelect,
  StockStatusSelect,
  FilterBadge,
  InlineFilters,
  CompactFilters,
  ModalFilters
};

export default InventoryFilters;
